import * as Measurement from 'core/messaging/measurement/measurement';
import { ActivitiesMessagingClient } from 'core/messaging/ActivitiesMessagingClient';
import { IIndex, IMeasurementAsync, IMoment, IPerformanceClientConfig, PerformanceClient } from '@agoda/performance-client';
import { resourceAggregates } from 'core/Performance/resourceAggregates';
import { PerformanceMeasurementName } from 'core/constant/Performance';
import { getBootstrapConfig, getPerformance, getPerformanceClient, getPerformanceObserver, setPerformance } from 'core/window/windowService';
import { pathname } from 'core-framework/window/location';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { isIos } from 'core-framework/DeviceInfo/DeviceInfo';
import { ActivitiesPageTypes, getPageNameFromPageTypeId } from 'core/constant/analytics/AnalyticsPageType';

export function configurePerformanceClient(bootstrap: BootstrapConfig, pageTypeId: ActivitiesPageTypes) {
    const pageConfig = bootstrap.pageConfig;
    const performanceClientConfig: IPerformanceClientConfig = {
        userId: bootstrap.userId,
        memberId: bootstrap.memberId,
        cid: pageConfig.cid,
        aid: pageConfig.aid,
        trafficGroupId: pageConfig.trafficGroupId,
        clientId: 1,
        devicePlatformId: bootstrap.userDevice.devicePlatformId,
        pageTypeId: pageConfig.pageTypeId,
        languageId: bootstrap.language.languageId,
        origin: pageConfig.origin,
        whiteLabelId: bootstrap.whiteLabelId,
        isBot: bootstrap.isBot,
        os: bootstrap.userDevice.mobileDeviceType,
        botName: bootstrap.requestProfile?.knownBotName,
        webView: bootstrap.isWebView,
        experiments: { ...bootstrap.experiments, collectINPMetricsBreakDown: false },
    };

    const perfClient = getPerformanceClient();
    if (perfClient) {
        // Update configuration on page change
        perfClient.updateConfig(performanceClientConfig);
        return;
    }

    setPerformance(new PerformanceClient(ActivitiesMessagingClient, performanceClientConfig));
}

let interactive: IMeasurementAsync<IMoment>;
let clickFirst: IMeasurementAsync<IMoment>;
let scrollFirst: IMeasurementAsync<IMoment>;
let paintLargest: IMeasurementAsync<IMoment>;
let totalBlockingTime: IMeasurementAsync<IIndex>;
const ResolvedPromise = Promise.resolve();

function measure(isNavigation = false) {
    const performanceClient = getPerformanceClient();

    clickFirst = performanceClient.measureClickFirst({}, isNavigation);
    scrollFirst = performanceClient.measureScrollFirst({}, isNavigation);
    paintLargest = performanceClient.measurePaintLargest({}, isNavigation);
}

export function measurePerformancePageLandingAsync() {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    Promise.resolve().then(() => {
        measureLanding();
    });
}

export function measureLanding() {
    const performanceClient = getPerformanceClient();
    if (!performanceClient) return;

    /**
     * isNavigation should default to false
     * when user navigate you then set it to true
     * isNavigation = true, only in case of hard navigation
     * hard nav should load a new file when you change page (like going back to agoda)
     * Activity funnel might not load new files because of asset caching.
     * https://agoda.slack.com/archives/C1FMDKUCW/p1675759916972749?thread_ts=1675334630.220549&cid=C1FMDKUCW
     */
    const isNavigation = false;

    measure(isNavigation);

    const config = getBootstrapConfig();
    const tags = {
        browserDetails: config.browser.browserName,
        isWebView: config.browser.isWebView,
        nonIosLcpSupport: nonIosLcpSupport(),
    };

    performanceClient.measureDocumentLoad(ResolvedPromise);
    performanceClient.measurePaintFirst(ResolvedPromise);
    const agodaLcp = performanceClient.measureAgodaLcp(tags, isNavigation);
    const alcpPromise = agodaLcp.await();

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    alcpPromise.then(() => {
        performanceClient.measureCumulativeLayoutShift(tags);
        interactive = performanceClient.measureInteractive(ResolvedPromise, {});
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        interactive.await().then(() => {
            totalBlockingTime = performanceClient.measureTotalBlockingTime(interactive.await(), {}, isNavigation);
        });
        performanceClient.measureResourceAggregates(ResolvedPromise, resourceAggregates(), {}, isNavigation);
    });

    performanceClient.measureTimeToFirstByte({}, isNavigation);
    performanceClient.measureInputFirstDelay(tags, isNavigation);
    performanceClient.measureNetworkInformation();
}

function nonIosLcpSupport(): boolean {
    if (isIos()) return true;
    const performanceObserver = getPerformanceObserver();
    if (!performanceObserver || !performanceObserver.supportedEntryTypes) return false;
    return performanceObserver.supportedEntryTypes.includes(PerformanceMeasurementName.LargestContentfulPaint) || false;
}

export function measureFinishRender(tags: Record<string, string | boolean | number | undefined>, pageTypeId: ActivitiesPageTypes, timestamp?: number) {
    if (!tags.url) tags.url = pathname();
    if (!tags.pageTypeId) tags.pageTypeId = pageTypeId;

    if (getPerformance().now) {
        const value = timestamp || getPerformance().now();
        Measurement.send(PerformanceMeasurementName.RumFinRenderAbs, value, pageTypeId);
        Measurement.send(PerformanceMeasurementName.CriticalFinish, value, pageTypeId);

        const performanceClient = getPerformanceClient();
        if (performanceClient) {
            const pageName = getPageNameFromPageTypeId(pageTypeId);
            performanceClient.measureCriticalMark(pageName.toLowerCase(), tags, timestamp, Date.now());
        }
    }
}

export function measureNavigation(pageTypeId: ActivitiesPageTypes) {
    const performanceClient = getPerformanceClient();
    if (!performanceClient) return;
    if (!pageTypeId) return;

    if (interactive) interactive.cancel();
    if (clickFirst) clickFirst.cancel();
    if (scrollFirst) scrollFirst.cancel();
    if (paintLargest) paintLargest.cancel();
    if (totalBlockingTime) totalBlockingTime.cancel();

    performanceClient.updateConfig({ pageTypeId });

    measure(true);
}
